import type { RunResult } from '../core/types';

/**
 * 플랫폼 계층 — 게임 코드는 여기만 보고, 디스코드를 몰라도 된다.
 *
 * 3단계: 디스코드 액티비티 안에서 돌면 `DiscordPlatform`,
 * 그 밖(개발 서버·브라우저 단독)에서는 `OfflinePlatform`.
 *
 * ## 판마다 두 번 부른다
 *
 * 1. 판이 시작될 때 `startRun()` — 서버에 **세션**을 연다
 * 2. 판이 끝나면 `submitScore()` — 그 세션으로 점수를 보낸다
 *
 * 순서가 중요하다. 서버는 "생존시간 ≤ 세션을 연 뒤 흐른 시간" 인지 본다.
 * 끝날 때 세션을 열면 흐른 시간이 0이라 **멀쩡한 판이 전부 거부된다**.
 */

export interface PlatformUser {
  id: string;
  username: string;
  globalName?: string;
}

export type SubmitOutcome =
  | { ok: true; best: boolean; rank?: number; score?: number }
  | { ok: false; reason: string };

export interface Platform {
  /** 디스코드 없이 도는 중인지 (?dev=1 · 디스코드 밖 · 연결 실패) */
  readonly offline: boolean;
  /** 왜 오프라인인지 — 결과 화면에 그대로 보여 준다 */
  readonly offlineReason: string;
  readonly user: PlatformUser;
  /** 판이 시작될 때. 서버에 세션을 연다(실패해도 게임은 그대로 돈다). */
  startRun(): void;
  /** 한 판 결과 전송. */
  submitScore(result: RunResult): Promise<SubmitOutcome>;
}

const FAKE_USER: PlatformUser = {
  id: 'dev-local',
  username: '개발자',
  globalName: '개발 모드',
};

/** 점수 서버 주소. 디스코드 안에서는 `/api` 가 프록시를 타고 봇으로 간다. */
const API_BASE = (import.meta.env.VITE_API_BASE || '/api').replace(/\/$/, '');

/** 디스코드가 없을 때 — 게임은 그대로 되고 점수만 안 보낸다. */
export class OfflinePlatform implements Platform {
  readonly offline = true;
  readonly user = FAKE_USER;

  constructor(readonly offlineReason = '개발 모드 — 점수를 서버에 보내지 않았다.') {}

  startRun(): void {
    /* 보낼 데가 없다 */
  }

  async submitScore(_result: RunResult): Promise<SubmitOutcome> {
    return { ok: false, reason: 'offline' };
  }
}

// ── 디스코드 ──────────────────────────────────────────────

/** 이 게임이 SDK 에서 쓰는 것만 추린 모양 — 시험용 가짜를 끼우기 쉽게 하려고 좁게 잡았다. */
export interface SdkLike {
  ready(): Promise<unknown>;
  commands: {
    authorize(args: {
      client_id: string;
      response_type: 'code';
      state: string;
      prompt: 'none';
      scope: string[];
    }): Promise<{ code: string }>;
    authenticate(args: { access_token: string }): Promise<{
      user: { id: string; username: string; global_name?: string | null };
      access_token: string;
    }>;
  };
}

async function postJson(path: string, body: unknown): Promise<Record<string, unknown>> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    const reason = typeof data.reason === 'string' ? data.reason : `http_${res.status}`;
    throw new Error(reason);
  }
  return data;
}

/**
 * 점수를 보내는 쪽의 공통 뼈대.
 *
 * 세션은 디스코드 access token 으로 연다. 토큰을 어디서 받았는지만 다르다:
 *   디스코드 액티비티 → SDK, 브라우저 링크 → 디스코드 웹 로그인(`weblogin.ts`).
 * 그 뒤(세션 하나로 한 판 제출)는 완전히 같다.
 */
abstract class SessionPlatform implements Platform {
  readonly offline = false;
  readonly offlineReason = '';
  abstract readonly user: PlatformUser;
  /** 판마다 새로 여는 세션. 판이 시작될 때 열어 두고 끝날 때 쓴다. */
  private session: Promise<string> | null = null;

  protected abstract openSession(): Promise<Record<string, unknown>>;

  startRun(): void {
    // 미리 시작해 두고 결과는 기다리지 않는다 — 실패해도 판은 그대로 돈다.
    this.session = this.openSession().then((d) => String(d.sessionId ?? ''));
    this.session.catch(() => {
      /* 제출할 때 다시 알린다 */
    });
  }

  async submitScore(result: RunResult): Promise<SubmitOutcome> {
    if (!this.session) return { ok: false, reason: 'no_session' };

    let sessionId: string;
    try {
      sessionId = await this.session;
    } catch (e) {
      // 판이 시작될 때 세션을 못 열었다. 지금 새로 열어 봐야 소용없다 —
      // 서버는 "생존시간 ≤ 세션을 연 뒤 흐른 시간" 을 보므로 끝나고 연 세션으로는 통과 못 한다.
      // 그래서 다시 보내기를 권하지 않고, 이 판은 저장 못 한다고 알린다.
      console.warn('세션을 열지 못했다', e);
      return { ok: false, reason: 'session_failed' };
    }

    try {
      const out = await postJson('/score', { sessionId, ...result });
      return {
        ok: true,
        best: out.best === true,
        rank: typeof out.rank === 'number' ? out.rank : undefined,
        score: typeof out.score === 'number' ? out.score : undefined,
      };
    } catch (e) {
      return { ok: false, reason: (e as Error).message || 'failed' };
    }
  }
}

/** 디스코드 액티비티 안 — 누구인지는 디스코드가 알려 준다. */
export class DiscordPlatform extends SessionPlatform {
  constructor(
    readonly user: PlatformUser,
    private readonly accessToken: string,
  ) {
    super();
  }

  protected openSession(): Promise<Record<string, unknown>> {
    return postJson('/session/start', { accessToken: this.accessToken });
  }
}

/** SDK 로 로그인까지 마치고 플랫폼을 만든다. 중간 어디서든 실패하면 던진다. */
export async function connect(sdk: SdkLike, clientId: string): Promise<DiscordPlatform> {
  await sdk.ready();
  const { code } = await sdk.commands.authorize({
    client_id: clientId,
    response_type: 'code',
    state: '',
    prompt: 'none',
    // 점수를 누구 것으로 저장할지만 알면 된다 — 그 이상은 안 받는다
    scope: ['identify'],
  });
  const { access_token } = (await postJson('/token', { code })) as { access_token?: string };
  if (!access_token) throw new Error('no_access_token');
  const auth = await sdk.commands.authenticate({ access_token });
  return new DiscordPlatform(
    {
      id: String(auth.user.id),
      username: auth.user.username,
      globalName: auth.user.global_name ?? undefined,
    },
    access_token,
  );
}

/**
 * 플랫폼 초기화.
 * 디스코드 안이 아니거나 연결에 실패하면 게임은 그대로 되고 점수만 안 간다.
 */
export async function initPlatform(dev: boolean): Promise<Platform> {
  const params = new URLSearchParams(location.search);

  // 개발 모드에서 `?mockdiscord=1` 이면 가짜 SDK 로 실제 연결 흐름을 그대로 밟는다
  // (점수 서버는 진짜로 부른다 — `python server/run_dev.py`).
  // `import.meta.env.DEV` 는 빌드할 때 false 로 박히므로 **배포 번들에서는 이 가지가 통째로 사라진다**
  // (dist 에 mockdiscord 조각이 안 생긴다 — 확인함).
  if (import.meta.env.DEV && dev && params.get('mockdiscord') === '1') {
    const { mockConnect } = await import('../dev/mockdiscord');
    try {
      return await mockConnect();
    } catch (e) {
      console.warn('[mockdiscord] 실패', e);
      return new OfflinePlatform(`가짜 디스코드 연결 실패: ${(e as Error).message}`);
    }
  }

  if (dev) return new OfflinePlatform();

  const clientId = import.meta.env.VITE_DISCORD_CLIENT_ID;
  if (!clientId) {
    console.warn('VITE_DISCORD_CLIENT_ID 가 없다 — client/.env 를 확인할 것');
    return new OfflinePlatform('설정이 없어 점수를 저장하지 않는다. (VITE_DISCORD_CLIENT_ID)');
  }

  // 디스코드 액티비티는 URL 쿼리에 frame_id 를 붙여서 열린다.
  // 그게 없으면 링크로 들어온 브라우저 — 디스코드 웹 로그인으로 같은 계정 기록을 쓴다.
  if (!params.has('frame_id')) {
    const { webLogin } = await import('./weblogin');
    try {
      const login = await webLogin(API_BASE, clientId);
      if (login) return new DiscordPlatform(login.user, login.accessToken);
      return new OfflinePlatform('로그인하지 않아 점수를 저장하지 않았다. 연습 판이었다.');
    } catch (e) {
      console.warn('웹 로그인 실패 — 점수 없이 돈다', e);
      return new OfflinePlatform(`로그인하지 못했다: ${(e as Error).message}`);
    }
  }

  try {
    const { DiscordSDK } = await import('@discord/embedded-app-sdk');
    return await connect(new DiscordSDK(clientId) as unknown as SdkLike, clientId);
  } catch (e) {
    console.warn('디스코드 연결 실패 — 점수 없이 돈다', e);
    return new OfflinePlatform(`디스코드에 연결하지 못했다: ${(e as Error).message}`);
  }
}
